import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraText } from '@mono/ui-components/ensigns';
import { SpecraDateField, SpecraPageHeader, SpecraSwitchField } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useParams, useSearchParams } from 'react-router-dom';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { registryHref } from '../registryHref';
import { CompanyFamilyView } from './company/CompanyFamily';
import { useCompanyFamily } from './company/hooks';
/**
 * registries/companies/:id/family (REG-62): the whole corporate family around
 * one company. "As of" shows the family on a past date; "Show ended links"
 * adds history. Both live in the URL so a link reproduces the view.
 */ export function CompanyFamilyPage() {
    const { id = '' } = useParams();
    const [params, setParams] = useSearchParams();
    const asOf = params.get('asOf') ?? '';
    const includeEnded = params.get('history') === '1';
    const family = useCompanyFamily(id, asOf, includeEnded);
    const name = family.data?.nodes.find((n)=>n.isFocus)?.name;
    const setParam = (key, value)=>{
        const p = new URLSearchParams(params);
        if (value) p.set(key, value);
        else p.delete(key);
        setParams(p, {
            replace: true
        });
    };
    const status = family.isPending ? 'loading' : family.isError ? 'error' : family.data ? 'ready' : 'unavailable';
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        kindLabel: "Companies",
        tab: "companies",
        title: name,
        status: status,
        error: family.error,
        onRetry: ()=>void family.refetch(),
        subPage: {
            label: 'Family',
            recordHref: registryHref({
                kind: 'COMPANY',
                id
            }) ?? `/registries/companies/${id}`
        },
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            eyebrow: "Company · corporate family",
            title: name ?? 'Company',
            description: family.data?.rootCompanyId && family.data.rootCompanyId !== family.data.focusCompanyId ? `Part of ${family.data.nodes.find((n)=>n.companyId === family.data?.rootCompanyId)?.name ?? 'a larger family'}` : 'Parent, subsidiaries, acquisitions, JVs and sales channels.'
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-6",
            "data-testid": Testids.registryCompany.familyPage,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-end gap-6",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraDateField, {
                            label: "As of",
                            hint: "Empty = today.",
                            value: asOf,
                            onValueChange: (v)=>setParam('asOf', v)
                        }),
                        /*#__PURE__*/ _jsx(SpecraSwitchField, {
                            label: "Show ended links",
                            description: "Sold subsidiaries, dissolved JVs, old names.",
                            controlProps: {
                                checked: includeEnded,
                                onCheckedChange: (on)=>setParam('history', on ? '1' : '')
                            }
                        })
                    ]
                }),
                family.data && /*#__PURE__*/ _jsx(CompanyFamilyView, {
                    family: family.data
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "meta",
                    tone: "muted",
                    children: "Record or change events on each company's page under Corporate history."
                })
            ]
        })
    });
}
